import { useEffect, useState, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { postWithoutTokenAPI } from "../util/Api";
import { useDispatch } from "react-redux";
import { setUserId } from "../redux/userSlice";
import { Header } from "./Header";
import toast, { Toaster } from "react-hot-toast";
import "../styles/LoginPage.css";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

export function Loginverify({ onNavigate }) {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(30);
  const inputsRef = useRef([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const Mno = location.state?.mobile;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Handle OTP input
  const handleChange = (index, value) => {
    if (/^\d$/.test(value)) {
      // Only allow one digit
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to next input automatically
      if (index < 3 && inputsRef.current[index + 1]) {
        inputsRef.current[index + 1].focus();
      }
    } else if (value === "") {
      // Allow backspace clearing
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      if (inputsRef.current[index - 1]) {
        inputsRef.current[index - 1].focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const enteredOtp = otp.join("");
    if (enteredOtp.length < 4) {
      setError(`${t("Please enter the OTP")}`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        mobile_number: String(Mno).trim(),
        otp_code: enteredOtp,
      };
      const response = await postWithoutTokenAPI("/auth/verify-otp", payload);

      if (response?.data) {
        const verifiedUserId = response?.data;
        dispatch(setUserId(verifiedUserId));
        localStorage.setItem(
          "UserId",
          JSON.stringify({
            id: verifiedUserId,
            timestamp: new Date().toISOString(),
          })
        );
        if (!response?.data?.profile_completed) {
          toast.success(`${t("OTP Verified Successfully")}`);
          navigate("/register", {
            state: { verifiedUser: response.data.accessToken },
          });
        } else {
          toast.success(`${t("Login Successfully")}`);
          navigate("/");
        }
      } else {
        toast.error(`${t("Invalid OTP. Please try again")}`);
      }
    } catch {
      toast.error(`${t("Something went wrong. Please try again later")}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const payload = { mobile_number: String(Mno).trim() };
      await postWithoutTokenAPI("/auth/send-otp", payload);
      setOtp(["", "", "", ""]);
      if (inputsRef.current[0]) inputsRef.current[0].focus();
      setTimer(30);
      setError("");
      toast.success(`${t("OTP resent successfully")}`);
    } catch {
      setError(`${t("Failed to resend OTP. Try again later")}`);
    } finally {
      setResending(false);
    }
  };
  // Back button handler
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const [currentLang, setCurrentLang] = useState("en");

  const changeLanguage = () => {
    const newLang = currentLang === "en" ? "ta" : "en";
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };
  return (
    <>
      {/* <Header /> */}
      <div className="bg-white shadow-sm border-b border-border">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full flex justify-between items-center h-16">
            {/* Back Button on Left End */}
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-medium">{t("Back")}</span>
            </button>

            <div className="w-20">
              <Button
                variant="ghost"
                size="sm"
                onClick={changeLanguage}
                className="avan-avala-language-btn hidden md:flex items-center space-x-1 text-gray-600 cursor-pointer hover:text-orange-500"
              >
                <Languages className="w-4 h-4" />
                <span className="avan-avala-language-text text-sm font-medium">
                  {currentLang === "en" ? "தமிழ்" : "English"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
      <div className="login-wrapper">
        <div className="login-left">
          <ImageWithFallback
            src="/Images/authentic-indian-bride-groom.jpg"
            alt="Couple"
            className="login-image"
          />
          <div className="login-overlay">
            <h2>{t("Verify Your Account")}</h2>
          </div>
        </div>

        <div className="login-right">
          <Card className="login-card">
            <CardHeader className="text-center">
              <div className="image">
                <img src="/Images/avanaval.png" alt="AvanAval" />
              </div>
              <CardTitle className="text-2xl font-bold">
                {t("Verify OTP")}
              </CardTitle>
              <p>{t("enterOtp", { Mno })}</p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="login-form">
                <div className="otp-inputs flex justify-center gap-2 mb-4">
                  {otp.map((digit, idx) => (
                    <Input
                      key={idx}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      type="text"
                      value={digit}
                      onChange={(e) => handleChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      maxLength={1}
                      className={`text-center text-lg font-semibold w-12 ${
                        error ? "input-error" : ""
                      }`}
                    />
                  ))}
                </div>
                {error && (
                  <span className="text-red-500 block text-center mb-2">
                    {error}
                  </span>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className=" bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 cursor-pointer"
                >
                  {loading ? t("Verifying...") : t("Verify OTP")}
                </Button>

                <div className="resend-section text-center mt-4">
                  {timer > 0 ? (
                    <p>
                      {t("Didn't receive OTP? Resend in")}{" "}
                      <span className="text-red-500 font-semibold">
                        {timer}s
                      </span>
                    </p>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resending}
                      className=" bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 cursor-pointer"
                    >
                      {resending ? t("Resending...") : t("Resend OTP")}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
